import { viewerClosingProgress, type ClosingChecklist } from '../components/closing/closing-model';
import { academicsFromStored, queensAcademicsComplete } from './queens-academics';
import type { GroupProductPreferenceSide, ProspectGroupPortalData, ProspectMeData } from '../services/tenantApi';
import type { PageProgress } from '../contexts/ProspectPageProgressContext';

const RANKED_PREFERENCE_KEYS = [
  'bathrooms',
  'laundry',
  'kitchen',
  'commonSpace',
  'parking',
  'furnished',
  'enSuiteBathroom',
  'outdoorSpace',
  'rentPerBedroom',
] as const;

function splitDisplayName(displayName: string | null | undefined): { firstName: string; lastName: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function preferencesAreComplete(side: GroupProductPreferenceSide | null | undefined): boolean {
  const values = side?.values;
  const priority = side?.priority ?? [];
  if (!values?.rentPerBedroom) return false;
  const rent = values.rentPerBedroom;
  const everyChoiceSet =
    (values.bedrooms?.length ?? 0) > 0 &&
    (values.bathrooms?.length ?? 0) > 0 &&
    values.laundry != null &&
    values.parking != null &&
    values.furnished != null &&
    values.enSuiteBathroom != null &&
    values.outdoorSpace != null &&
    (rent.min != null || rent.max != null);
  const rankingMoved = RANKED_PREFERENCE_KEYS.some((key) => priority.includes(key));
  return everyChoiceSet && rankingMoved;
}

function groupHasAnotherPerson(group: ProspectGroupPortalData | null | undefined, selfPersonId: string): boolean {
  if (!group) return false;
  if (group.soloDeclaredAt) return true;
  return (group.members ?? []).some(
    (member) =>
      member.personId !== selfPersonId &&
      (member.status === 'INVITED' || member.status === 'ACCEPTED'),
  );
}

/**
 * Same five cards My Information uses. An unsaved school year counts as done
 * because the form opens on "1st year".
 */
export function myInformationProgress(
  prospect: ProspectMeData,
  group: ProspectGroupPortalData | null | undefined,
): PageProgress {
  const name = splitDisplayName(prospect.displayName);
  const basicDetailsDone =
    name.firstName.length > 0 &&
    name.lastName.length > 0 &&
    (prospect.verifiedVia === 'phone'
      ? Boolean(prospect.email?.trim())
      : (prospect.phone?.replace(/\D/g, '').length ?? 0) >= 10);
  const schoolDone = queensAcademicsComplete(academicsFromStored(prospect));
  const notificationsDone =
    Boolean(prospect.primaryBedroom || prospect.secondaryBedroom || prospect.tertiaryBedroom) ||
    prospect.generalNotifications;
  const preferencesDone = preferencesAreComplete(group?.productPreferences?.stated);
  const groupPersonAdded = groupHasAnotherPerson(group, prospect.personId);
  const cards = [basicDetailsDone, schoolDone, preferencesDone, notificationsDone, groupPersonAdded];
  return { completed: cards.filter(Boolean).length, total: cards.length };
}

/** Video watched (half) plus at least one discovery call marked Showed. */
export function discoveryCallProgress(videoDone: boolean, callShowed: boolean): PageProgress {
  return { completed: Number(videoDone) + Number(callShowed), total: 2 };
}

function doneOrNot(done: boolean): PageProgress {
  return { completed: done ? 1 : 0, total: 1 };
}

/** Complete once the group has saved at least one home. */
export function shortlistProgress(unitCount: number): PageProgress {
  return doneOrNot(unitCount > 0);
}

/** Complete once at least one tour is booked. Cancelled tours do not count. */
export function tourBookingProgress(bookingIds: string[]): PageProgress {
  return doneOrNot(new Set(bookingIds.filter(Boolean)).size > 0);
}

/**
 * The step is each home whose tour has already happened. It fills as those
 * analyses are finished, and it is complete only when every toured home is done.
 */
export function unitAnalysisProgress(
  units: Array<{ availability: 'ready' | 'upcoming'; analyzed: boolean }>,
): PageProgress {
  const toured = units.filter((unit) => unit.availability === 'ready');
  if (toured.length === 0) return { completed: 0, total: 1 };
  return { completed: toured.filter((unit) => unit.analyzed).length, total: toured.length };
}

/** Complete once the group is selected for a home. */
export function selectionProgress(selectedUnitId: string | null): PageProgress {
  return doneOrNot(Boolean(selectedUnitId));
}

/**
 * Empty until the group is selected for a home. After that, the ring is the
 * viewer's three steps plus the three group steps.
 */
export function leaseSigningProgress(checklist?: ClosingChecklist | null): PageProgress {
  return viewerClosingProgress(checklist);
}
